import { useState, useEffect, useRef } from 'react';
import useDepsInterval from 'components/hooks/useDepsInterval';
import { isTextPlaceholder, isTOCPage } from 'editor-utils/template';

import useEditorContext from '../../context/editorContext';
import useEditorItemContext from '../../context/editorItemContext';
import generateTocData from '../../generator/generateTocData';

const useTOC = (editorItem, template) => {
  const editorContext = useEditorContext();
  const { tocPageIds, enabledTOC } = useEditorItemContext();

  const [state, setState] = useState('');

  const editorItemRef = useRef(editorItem);
  useEffect(() => {
    editorItemRef.current = editorItem;
  }, [editorItem]);

  const saveIsRequired = useRef(false);
  const connectionIsActive = useRef(false);

  const callback = async () => {
    if (enabledTOC) {
      // REAL DATA
      const item = editorItemRef.current;

      const { data: newTOCdata, error } = await generateTocData(
        editorContext.accessToken,
        template,
        item,
        tocPageIds,
      );
      if (error) {
        return 'error';
      }

      editorContext.updateTOCPages(item.id, tocPageIds, newTOCdata, template.cover);
    }
    return 'synced';
  };

  const update = async () => {
    saveIsRequired.current = true;

    while (!connectionIsActive.current && saveIsRequired.current) {
      setState('syncing');
      connectionIsActive.current = true;
      saveIsRequired.current = false;
      const result = await callback();
      setState(result);
      connectionIsActive.current = false;
    }
  };

  // TOC
  useDepsInterval(
    () => {
      update();
    },
    3000,
    [
      tocPageIds,
      enabledTOC,
      (editorItem.pages ?? []).reduce((pages, page) => {
        if (!isTOCPage(template, page.id)) {
          pages.push({
            id: page.id,
            caption: page.caption,
            tocSkip: page.tocSkip,
            placeholders: (page.placeholders ?? []).reduce((placeholders, placeholder) => {
              if (isTextPlaceholder(placeholder)) {
                placeholders.push({
                  placeholder: placeholder.id,
                  value: placeholder.data?.value ?? '',
                });
              }
              return placeholders;
            }, []),
          });
        }
        return pages;
      }, []),
    ],
  );

  return {
    state,
    update,
  };
};

export default useTOC;
